import { useMutation } from '@tanstack/react-query';
import { Mail, Send } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuthConfig } from '../../lib/firebase';
import { useMe, useUpdateSettings } from '../../hooks/useMe';
import { Button, Card, CardBody, CardDivider, CardHeader, Notice, Select, useToast } from '../ui';
import { Rows, SettingRow, SwitchRow } from './SettingsPrimitives';

const hourLabel = (h: number) => new Date(2000, 0, 1, h).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

/** Morning email of what needs you (personal). Needs the server's own mail sender. */
export function DailySummaryCard() {
  const me = useMe().data;
  const update = useUpdateSettings();
  const toast = useToast();
  const systemMail = useAuthConfig().data?.systemMail ?? true;
  const sendNow = useMutation({
    mutationFn: () => api('POST /api/summary/send-now'),
    onSuccess: () => toast.success(`Sent — check ${me?.user.email}.`),
    onError: (e) => toast.error(e),
  });
  if (!me) return null;
  const ds = me.settings.dailySummary;
  return (
    <Card>
      <CardHeader icon={<Mail className="h-5 w-5" />} title="Daily summary email" description={`Each morning: what needs your reply, follow-ups with no answer, and what's assigned to you — sent to ${me.user.email}. Just for you.`} />
      <CardDivider className="mt-5" />
      <CardBody className="space-y-4 !py-5">
        {!systemMail && <Notice tone="warn">The server can't send email yet, so summaries are paused. An admin needs to set up the server's email sender (SYSTEM_SMTP_URL).</Notice>}
        <Rows>
          <SwitchRow id="daily-summary" checked={ds.enabled} onChange={(v) => update.mutate({ dailySummary: { ...ds, enabled: v } })} label="Send me a daily summary" description={`In your time zone (${me.settings.timezone}).`} />
          <SettingRow
            label="Send at"
            description="Around this time each day."
            control={
              <Select aria-label="Send at" value={String(ds.hour)} disabled={!ds.enabled} onChange={(e) => update.mutate({ dailySummary: { ...ds, hour: Number(e.target.value) } })}>
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>{hourLabel(h)}</option>
                ))}
              </Select>
            }
          />
          <SettingRow
            label="Try it"
            description="Sends today's summary to you now."
            control={<Button size="sm" variant="outline" icon={<Send className="h-4 w-4" />} loading={sendNow.isPending} disabled={!systemMail} onClick={() => sendNow.mutate()}>Send me one now</Button>}
          />
        </Rows>
      </CardBody>
    </Card>
  );
}
